/**
 * TDBPnLBreakdown — True 6-bucket × 3-city P&L tile.
 *
 * Reads `GET /api/tdb/mis/leader-cockpit?period_start=&period_end=`
 * which returns Mickky/Nihal's monthly MIS broken into 6 expense buckets
 * per city plus group totals.
 *
 * Renders:
 *   • Month picker (uses /api/tdb/mis/periods to populate)
 *   • Loud-alerts banner for screaming numbers
 *   • Group-level GOP card
 *   • 6-bucket × 3-city × Group matrix with RAG colour codes
 *   • Income line strip (3 cities side-by-side)
 *   • Source pill ("FROM MIS · uploaded 0d ago by Dipali")
 *
 * Empty state: "No MIS data yet — upload the monthly Excel via the
 * Cost Module" with a deep-link.
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, AlertTriangle, Upload, RefreshCw, FileSpreadsheet,
  Compass, ScrollText,
} from 'lucide-react';
import api from '@/lib/api';
import TDBPnLTrendSparkline from '@/components/TDBPnLTrendSparkline';

// ──────────────────────────────────────────────────────────────────
// Healthy-threshold doctrine — must mirror backend HEALTHY_PCT.
// Used purely to colour the bucket cells (the alerts banner is
// already computed server-side).
// ──────────────────────────────────────────────────────────────────
const HEALTHY_PCT = {
  food_cost:     [0, 8],
  manpower:      [0, 35],
  operating:     [0, 35],
  marketing:     [0.5, 99],
};

const BUCKET_ORDER = ['food_cost', 'manpower', 'utilities', 'operating', 'marketing', 'admin_gateway'];

const CITY_ORDER = ['Bengaluru', 'Mumbai', 'Gurgaon'];

const COLORS = {
  red:   { bg: '#FEF2F2', fg: '#991B1B', border: '#D4726A' },
  amber: { bg: '#FFFBEB', fg: '#78350F', border: '#BA7517' },
  green: { bg: '#ECFDF5', fg: '#14532D', border: '#2D8B5E' },
  grey:  { bg: '#F7F5F2', fg: '#6B7280', border: '#E5E1DC' },
};

function ragForBucket(bucketKey, pct) {
  const t = HEALTHY_PCT[bucketKey];
  if (!t) return 'grey';
  const [lo, hi] = t;
  if (bucketKey === 'marketing') {
    // marketing is "spend at least lo%" — RED if below floor
    if (pct < lo) return 'red';
    if (pct > hi) return 'amber';
    return 'green';
  }
  if (pct > hi * 1.3) return 'red';
  if (pct > hi) return 'amber';
  return 'green';
}

function ragForGop(gopPct) {
  if (gopPct >= 10) return 'green';
  if (gopPct >= 0) return 'amber';
  return 'red';
}

function fmtRupees(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '₹0';
  const abs = Math.abs(n);
  if (abs >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${n >= 0 ? '' : ''}${n.toFixed(1)}%`;
}

function periodLabel(period) {
  if (!period || !period.month_ym) return '';
  const [y, m] = period.month_ym.split('-');
  return new Date(Date.UTC(+y, +m - 1, 1))
    .toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}

function daysSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

export default function TDBPnLBreakdown({ className = '' }) {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');  // "period_start|period_end"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [doctrine, setDoctrine] = useState(() => {
    // Persist choice across page reloads — defaults to ScaleBoard (truth)
    try {
      const stored = window.localStorage.getItem('tdb_pnl_doctrine');
      return stored === 'source' ? 'source' : 'scaleboard';
    } catch { return 'scaleboard'; }
  });

  const setDoctrineAndPersist = useCallback((d) => {
    setDoctrine(d);
    try { window.localStorage.setItem('tdb_pnl_doctrine', d); } catch { /* noop */ }
  }, []);

  // Load list of periods once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/tdb/mis/periods');
        if (cancelled) return;
        const list = r.data?.periods || [];
        setPeriods(list);
        if (list.length > 0) {
          setSelectedKey(`${list[0].period_start}|${list[0].period_end}`);
        }
      } catch {
        if (!cancelled) setErr('Could not load MIS periods');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load cockpit for selected period + doctrine
  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const params = { doctrine };
      if (selectedKey) {
        const [ps, pe] = selectedKey.split('|');
        params.period_start = ps;
        params.period_end = pe;
      }
      const r = await api.get('/tdb/mis/leader-cockpit', { params });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load MIS');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedKey, doctrine]);

  useEffect(() => {
    if (selectedKey || periods.length === 0) {
      load();
    }
  }, [selectedKey, periods.length, doctrine, load]);

  const ok = !!data?.ok;
  const groupTotals = data?.group_totals || {};
  const byCity = data?.by_city || {};
  const alerts = data?.alerts || [];
  const validationWarnings = data?.validation_warnings || [];
  const uploadedDays = useMemo(() => daysSince(data?.uploaded_at), [data]);
  const isSourceDoctrine = (data?.doctrine === 'source');

  // EMPTY STATE
  if (!loading && (!data || !ok)) {
    return (
      <div
        data-testid="tdb-pnl-breakdown"
        className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-gray-500" />
            <h3 className="text-base font-semibold text-gray-900">True P&amp;L Breakdown</h3>
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">FROM MIS</span>
          </div>
        </div>
        <div
          data-testid="tdb-pnl-empty"
          className="text-center py-10 bg-amber-50 rounded-xl border border-amber-200"
        >
          <AlertTriangle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
          <div className="text-sm font-semibold text-amber-900 mb-1">
            No MIS data uploaded yet
          </div>
          <div className="text-xs text-amber-800 mb-4">
            Upload Mickky/Nihal&apos;s monthly MIS Excel to see the 6-bucket × 3-city P&amp;L.
          </div>
          <button
            data-testid="tdb-pnl-upload-cta"
            onClick={() => navigate('/tdb/cost')}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-amber-600 text-white hover:bg-amber-700"
          >
            <Upload className="w-3.5 h-3.5" />
            Open Cost Module
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="tdb-pnl-breakdown"
      className={`bg-white rounded-2xl border border-gray-200 p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-gray-700" />
          <h3 className="text-base font-semibold text-gray-900">True P&amp;L Breakdown</h3>
          <span
            data-testid="tdb-pnl-source-pill"
            className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
            title={`${data?.source_file_name || ''} · uploaded by ${data?.uploaded_by || '—'}`}
          >
            FROM MIS{uploadedDays !== null ? ` · ${uploadedDays}d ago` : ''}
          </span>
          {/* Doctrine badge — clear marker of which numbers user is looking at */}
          <span
            data-testid="tdb-pnl-doctrine-badge"
            className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
              isSourceDoctrine
                ? 'bg-amber-50 text-amber-800 border-amber-200'
                : 'bg-indigo-50 text-indigo-700 border-indigo-200'
            }`}
            title={data?.doctrine_note || ''}
          >
            {isSourceDoctrine ? '📜 Source doctrine' : '📐 ScaleBoard doctrine'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Doctrine toggle — switch between truth (default) and audit view */}
          <div
            data-testid="tdb-pnl-doctrine-toggle"
            className="inline-flex items-center text-[11px] rounded-md border border-gray-300 bg-white overflow-hidden"
          >
            <button
              data-testid="tdb-pnl-doctrine-scaleboard"
              onClick={() => setDoctrineAndPersist('scaleboard')}
              className={`px-2 py-1 ${!isSourceDoctrine ? 'bg-indigo-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              title="Revenue-weighted shared costs (truth view)"
            >
              <Compass className="w-3 h-3 inline mr-1" /> ScaleBoard
            </button>
            <button
              data-testid="tdb-pnl-doctrine-source"
              onClick={() => setDoctrineAndPersist('source')}
              className={`px-2 py-1 ${isSourceDoctrine ? 'bg-amber-600 text-white' : 'text-gray-700 hover:bg-gray-50'}`}
              title="Mickky's 1/3 equal split (parallel-run audit view)"
            >
              <ScrollText className="w-3 h-3 inline mr-1" /> Source
            </button>
          </div>
          <select
            data-testid="tdb-pnl-period-picker"
            value={selectedKey}
            onChange={(e) => setSelectedKey(e.target.value)}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white"
          >
            {periods.map((p) => {
              const key = `${p.period_start}|${p.period_end}`;
              const label = periodLabel(p);
              return (
                <option key={key} value={key}>{label || `${p.period_start} → ${p.period_end}`}</option>
              );
            })}
          </select>
          <button
            data-testid="tdb-pnl-refresh"
            onClick={load}
            className="text-xs px-2 py-1 rounded-md border border-gray-300 bg-white hover:bg-gray-50 flex items-center gap-1"
            title="Reload from MIS"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Source-doctrine warning — visible only in 'source' mode */}
      {isSourceDoctrine && (
        <div
          data-testid="tdb-pnl-source-doctrine-note"
          className="mb-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900 flex items-start gap-1.5"
        >
          <ScrollText className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <span className="font-semibold">Source doctrine view · </span>
            1/3 equal split on shared costs — diverges from current allocation policy.
            Switch to <span className="font-medium">ScaleBoard doctrine</span> for true numbers.
          </div>
        </div>
      )}

      {/* Validation chip strip — surfaces inter-branch / city-total / utility issues */}
      {validationWarnings.length > 0 && (
        <div
          data-testid="tdb-pnl-validation-strip"
          className="mb-3 flex flex-wrap gap-1.5"
        >
          {validationWarnings.slice(0, 6).map((w, i) => (
            <span
              key={i}
              data-testid={`tdb-pnl-validation-chip-${i}`}
              className={`text-[10px] px-2 py-0.5 rounded-full border ${
                w.severity === 'red'
                  ? 'bg-red-50 text-red-800 border-red-200'
                  : 'bg-amber-50 text-amber-800 border-amber-200'
              }`}
              title={w.message}
            >
              ⚠ {w.code}{w.city ? ` · ${w.city}` : ''}
            </span>
          ))}
        </div>
      )}

      {/* Loud alerts banner — only red alerts get screamed */}
      {alerts.filter(a => a.severity === 'red').length > 0 && (
        <div
          data-testid="tdb-pnl-loud-alerts"
          className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
            <div className="text-xs text-red-900 leading-snug">
              <div className="font-semibold mb-1">Loud alerts · {alerts.filter(a => a.severity === 'red').length}</div>
              <ul className="space-y-0.5">
                {alerts.filter(a => a.severity === 'red').slice(0, 4).map((a, i) => (
                  <li key={i} data-testid={`tdb-pnl-alert-${i}`}>• {a.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Group-level GOP card */}
      <div
        data-testid="tdb-pnl-group-gop"
        className="mb-4 p-4 rounded-xl border"
        style={{
          backgroundColor: COLORS[ragForGop(groupTotals.gop_pct || 0)].bg,
          borderColor: COLORS[ragForGop(groupTotals.gop_pct || 0)].border,
        }}
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-600 font-medium">
              {periodLabel(data)} · Group GOP
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <div
                className="text-3xl font-bold"
                style={{ color: COLORS[ragForGop(groupTotals.gop_pct || 0)].fg }}
              >
                {fmtRupees(groupTotals.gop || 0)}
              </div>
              <div
                className="text-sm font-medium flex items-center gap-1"
                style={{ color: COLORS[ragForGop(groupTotals.gop_pct || 0)].fg }}
              >
                {(groupTotals.gop_pct || 0) >= 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : (
                  <TrendingDown className="w-4 h-4" />
                )}
                {fmtPct(groupTotals.gop_pct)}
              </div>
            </div>
          </div>
          <div className="text-right text-xs text-gray-700">
            <div>Income: <span className="font-semibold">{fmtRupees(groupTotals.income || 0)}</span></div>
            <div>Expense: <span className="font-semibold">{fmtRupees(groupTotals.expense || 0)}</span></div>
          </div>
        </div>
      </div>

      {/* Income strip per city */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {CITY_ORDER.map((city) => {
          const c = byCity[city] || {};
          const cityRag = ragForGop(c.gop_pct || 0);
          return (
            <div
              key={city}
              data-testid={`tdb-pnl-city-${city.toLowerCase()}`}
              className="p-3 rounded-xl border"
              style={{ backgroundColor: COLORS[cityRag].bg, borderColor: COLORS[cityRag].border }}
            >
              <div className="text-xs font-medium text-gray-700">{city}</div>
              <div className="text-lg font-semibold mt-0.5" style={{ color: COLORS[cityRag].fg }}>
                {fmtRupees(c.gop || 0)}
              </div>
              <div className="text-[11px]" style={{ color: COLORS[cityRag].fg }}>
                GOP {fmtPct(c.gop_pct)} · Inc {fmtRupees(c.grand_total_income || 0)}
              </div>
            </div>
          );
        })}
      </div>

      {/* MoM margin % trend sparkline — gated on ≥3 v4 periods,
          placeholder until then. Honours the same doctrine toggle. */}
      <TDBPnLTrendSparkline doctrine={doctrine} />

      {/* 6-bucket × 3-city × Group matrix */}
      <div className="overflow-x-auto" data-testid="tdb-pnl-matrix">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left py-2 px-2 font-medium">Bucket</th>
              {CITY_ORDER.map((city) => (
                <th key={city} className="text-right py-2 px-2 font-medium">{city}</th>
              ))}
              <th className="text-right py-2 px-2 font-medium bg-gray-50 rounded-tr-lg">Group</th>
            </tr>
          </thead>
          <tbody>
            {BUCKET_ORDER.map((bk) => {
              const groupBucket = (groupTotals.buckets || {})[bk] || {};
              return (
                <tr
                  key={bk}
                  data-testid={`tdb-pnl-row-${bk}`}
                  className="border-t border-gray-100"
                >
                  <td className="py-2 px-2 font-medium text-gray-800">
                    <span className="mr-1">{groupBucket.icon || ''}</span>
                    {groupBucket.label || bk}
                  </td>
                  {CITY_ORDER.map((city) => {
                    const cityBucket = ((byCity[city] || {}).buckets || {})[bk] || { total: 0, pct_of_income: 0 };
                    const rag = ragForBucket(bk, cityBucket.pct_of_income || 0);
                    return (
                      <td
                        key={city}
                        data-testid={`tdb-pnl-cell-${bk}-${city.toLowerCase()}`}
                        className="text-right py-2 px-2 align-top"
                      >
                        <div className="font-medium text-gray-900">{fmtRupees(cityBucket.total || 0)}</div>
                        <div
                          className="text-[11px] inline-block px-1.5 py-0.5 rounded-full mt-0.5"
                          style={{
                            backgroundColor: COLORS[rag].bg,
                            color: COLORS[rag].fg,
                          }}
                        >
                          {fmtPct(cityBucket.pct_of_income || 0)}
                        </div>
                      </td>
                    );
                  })}
                  <td
                    data-testid={`tdb-pnl-group-${bk}`}
                    className="text-right py-2 px-2 align-top bg-gray-50 font-semibold"
                  >
                    <div className="text-gray-900">{fmtRupees(groupBucket.total || 0)}</div>
                    <div
                      className="text-[11px] inline-block px-1.5 py-0.5 rounded-full mt-0.5"
                      style={{
                        backgroundColor: COLORS[ragForBucket(bk, groupBucket.pct_of_income || 0)].bg,
                        color: COLORS[ragForBucket(bk, groupBucket.pct_of_income || 0)].fg,
                      }}
                    >
                      {fmtPct(groupBucket.pct_of_income || 0)}
                    </div>
                  </td>
                </tr>
              );
            })}
            {/* Unmapped (review) — only shows if parser found uncategorised lines */}
            {(groupTotals.buckets || {}).unmapped && (
              <tr data-testid="tdb-pnl-row-unmapped" className="border-t border-amber-200">
                <td className="py-2 px-2 text-amber-800">
                  <span className="mr-1">⚠️</span> Unmapped (review)
                </td>
                {CITY_ORDER.map((city) => {
                  const cb = ((byCity[city] || {}).buckets || {}).unmapped;
                  return (
                    <td key={city} className="text-right py-2 px-2 text-xs text-amber-800">
                      {cb ? fmtRupees(cb.total || 0) : '—'}
                    </td>
                  );
                })}
                <td className="text-right py-2 px-2 bg-amber-50 text-amber-900 font-semibold">
                  {fmtRupees((groupTotals.buckets.unmapped || {}).total || 0)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer micro-meta */}
      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-500">
        <div>
          Source: <span className="font-medium text-gray-700">{data?.source_file_name || '—'}</span>
          {' · '}by <span className="font-medium text-gray-700">{data?.uploaded_by || '—'}</span>
        </div>
        <button
          data-testid="tdb-pnl-open-cost-module"
          onClick={() => navigate('/tdb/cost')}
          className="text-emerald-700 hover:text-emerald-900 font-medium"
        >
          Open Cost Module →
        </button>
      </div>

      {err && (
        <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
          {err}
        </div>
      )}
    </div>
  );
}
